// @ts-strict-ignore
import React, { useEffect, useState } from 'react'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'
import { Line } from 'react-chartjs-2'
import window from '../window'
import { Card, Field, FieldGroup, Label, Input, Button, RadioGroup, Radio } from '../../../../atoms'
import Callout from '../../../../components/callout'
import { drawdown } from '../pensions-calculator/calculation'
import { lineOptions } from '../pensions-calculator/line-options'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend)

const formatThousands = (n) => n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')

const PensionsDrawdownCalculator = ({
  params: { pot: potParam, retirement: retirementParam, income: incomeParam },
  warningText,
}) => {
  const [retirementAge, setRetirementAge] = useState(retirementParam)
  const [pensionPot, setPensionPot] = useState(potParam)
  const [targetAnnualIncome, setTargetAnnualIncome] = useState(incomeParam)
  const [includeFullStatePension, setIncludeFullStatePension] = useState(true)
  const [inflation, setInflation] = useState(2)
  const interest = 3
  const [chartData, setChartData] = useState(null)

  useEffect(() => {
    const urlSearchParams = new URLSearchParams(window.location?.search)
    const entries = Object.fromEntries(urlSearchParams.entries())
    const entriesParsed = Object.entries(entries).reduce(
      (obj, [key, val]) => ({ ...obj, [key]: parseInt(val) }),
      {},
    )
    setPensionPot(entriesParsed.pot ?? potParam)
    setRetirementAge(entriesParsed.retirement ?? retirementParam)
    setTargetAnnualIncome(entriesParsed.income ?? incomeParam)
  }, [retirementParam, potParam, incomeParam])

  function calculatePensionDrawdown() {
    setChartData(
      drawdown({
        retirementAge,
        pensionPot,
        targetAnnualIncome,
        includeFullStatePension,
        inflation,
        interest,
      }),
    )
  }

  return (
    <div className='PensionsCalculator Calculator'>
      <Card variant='plum'>
        <div className='inner-card'>
          <Field stack={true}>
            <Label>Retirement age</Label>
            <Input
              value={retirementAge}
              onChange={(e) => setRetirementAge(e.target.value)}
              type='number'
            />
          </Field>

          <Field stack={true}>
            <Label>Target annual income</Label>
            <Input
              value={targetAnnualIncome}
              onChange={(e) => setTargetAnnualIncome(e.target.value)}
              type='text'
              prefix='£'
              format='thousands'
            />
          </Field>

          <Field stack={true}>
            <Label>Pension pot</Label>
            <Input
              value={pensionPot}
              onChange={(e) => setPensionPot(e.target.value)}
              type='text'
              prefix='£'
              format='thousands'
            />
          </Field>
        </div>

        <h2>Get a more accurate estimate by answering these questions</h2>

        <Field stack={true}>
          <Label>Include full state pension?</Label>
          <RadioGroup>
            <Radio
              value={'true'}
              name='include-full-state-pension'
              currentValue={includeFullStatePension.toString()}
              onChange={(e) => {
                setIncludeFullStatePension(e.target.value === 'true')
              }}
            >
              Yes
            </Radio>
            <Radio
              name='include-full-state-pension'
              value={'false'}
              currentValue={includeFullStatePension.toString()}
              onChange={(e) => {
                setIncludeFullStatePension(e.target.value === 'true')
              }}
            >
              No
            </Radio>
          </RadioGroup>
        </Field>

        <Field stack={true}>
          <Label>Inflation (%)</Label>
          <FieldGroup>
            <Input
              value={inflation}
              onChange={(e) => setInflation(e.target.value)}
              type={'number'}
            />
          </FieldGroup>
        </Field>

        <Button
          variant={chartData === null ? 'primary' : 'secondary'}
          onClick={calculatePensionDrawdown}
        >
          {chartData === null ? 'Calculate drawdown' : 'Recalculate drawdown'}
        </Button>

        {chartData !== null && (
          <div className='pensions-chart'>
            <h3>
              If you take <span>£{formatThousands(chartData.yearlyWithdrawal)}</span> a year or{' '}
              <span>£{formatThousands(chartData.monthlyWithdrawal)}</span> a month, this will last
              you until you turn <span>{chartData.finalPayAge}</span>.
            </h3>
            <div className='pensions-chart-graph'>
              <Line
                options={lineOptions([])}
                data={{
                  labels: chartData.ages.map((a) => `Age ${a}`),
                  datasets: [
                    {
                      label: 'Actual pension',
                      data: chartData.pensionPotData,
                      borderColor: '#DB4D75',
                      backgroundColor: '#DB4D75',
                      spanGaps: true,
                    },
                  ],
                }}
              />
            </div>
          </div>
        )}
      </Card>
      <Callout variant='platinum-warning' bold={true} text={warningText} />
    </div>
  )
}

export default PensionsDrawdownCalculator
